import {
  WesterosServiceCreateDocument,
  WesterosServiceDeleteDocument,
  WesterosServiceGetByIdDocument,
  WesterosServiceListDocument,
  WesterosServicesPaginatedDocument,
  WesterosServiceUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { Optional, PartialNullable, ResourceEnum, ScopeEnum } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";
import {
  IWesterosServiceListVars,
  IWesterosServiceListReturnType,
  ICreateWesterosServiceVars,
  IWesterosServiceModel,
  IGetWesterosServicesReturnType,
  IUpdateWesterosServiceVars,
} from "./types";

export async function getWesterosServicesList(
  variables: IWesterosServiceListVars
): Promise<IWesterosServiceListReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosServicesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosServicesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createWesterosService(
  variables: ICreateWesterosServiceVars
): Promise<Optional<Pick<IWesterosServiceModel, "westerosServiceId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosServiceCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "WesterosService",
      fieldNames: ["westerosServicesPaginated", "westerosServiceList"],
    });

    return res?.data?.westerosServiceCreate;
  } catch (error) {
    throw error;
  }
}

export async function getWesterosService(
  westerosServiceId: number
): Promise<PartialNullable<IWesterosServiceModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosServiceGetByIdDocument,
      variables: {
        westerosServiceId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosServiceGetById;
  } catch (error) {
    throw error;
  }
}

export async function getWesterosServices(
  scope: ScopeEnum,
  resource: ResourceEnum
): Promise<IGetWesterosServicesReturnType[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: WesterosServiceListDocument,
      variables: {
        scope,
        resource,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.westerosServiceList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function updateWesterosService(
  variables: IUpdateWesterosServiceVars
): Promise<Optional<Pick<IWesterosServiceModel, "westerosServiceId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosServiceUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "WesterosService",
      id: variables.westerosServiceId.toString(),
      fieldNames: [
        "westerosServicesPaginated",
        "westerosServiceList",
        "westerosServiceGetById",
      ],
    });

    return res?.data?.westerosServiceUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteWesterosService(
  westerosServiceId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: WesterosServiceDeleteDocument,
      variables: {
        westerosServiceId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "WesterosService",
      id: westerosServiceId.toString(),
      fieldNames: [
        "westerosServicesPaginated",
        "westerosServiceList",
        "westerosServiceGetById",
      ],
    });

    return Boolean(res?.data?.westerosServiceDelete);
  } catch (error) {
    throw error;
  }
}
